import { AbgabebelegPosition } from '../../abgabebelege/classes/abgabebeleg-position';
import { ChangeDetectorRef, Component, Inject, OnDestroy } from '@angular/core';
import { FormControl, UntypedFormControl, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Subscription } from 'rxjs';
import { MessageService } from '../../../services/message-service/message.service';
import { Tierliste } from '../../gruppe-tier/classes/tierliste';
import { GruppenService } from '../gruppen.service';
import { KieneUtils } from '../../../utils/KieneUtils';
import { AbgangDownloadRequestPojo } from './gruppe-abgang-download-dialog';
import { KieneApi, KieneBackendApiService } from '../../../services/backend-api/kiene-backend-api.service';
import { API_BASE_URL_SERVICE } from '../../../kiene-core.config';
import { HttpParams } from '@angular/common/http';

const SORT_ABTEIL = 'gt.abteil_abgang';

@Component({
    selector: 'kiene-gruppe-abgang-download-dialog',
    templateUrl: './gruppe-abgang-download-dialog.component.html',
    styleUrls: ['./gruppe-abgang-download-dialog.component.scss'],
})
export class GruppeAbgangDownloadDialogComponent implements OnDestroy {
    tierlistCtrl: UntypedFormControl = new UntypedFormControl(null, Validators.required);
    showBarcodeCtrl: UntypedFormControl = new UntypedFormControl(0);
    showGruppendetailsCtrl: UntypedFormControl = new UntypedFormControl(0);
    showTiergruppeCtrl: UntypedFormControl = new UntypedFormControl(0);
    showEinstalldatumCtrl: UntypedFormControl = new UntypedFormControl(0);
    /** Default Ja: Abteil column + sort visible in Kundenportal / WebView. */
    showAbteilCtrl: UntypedFormControl = new UntypedFormControl(1);
    einstalldatumSortCtrl = new FormControl<'none' | 'asc' | 'desc'>('none');
    geschlechtSortCtrl = new FormControl<'none' | 'w' | 'm'>('none');
    splitGeschlechtCtrl = new FormControl(0);
    addScansCtrl: UntypedFormControl = new UntypedFormControl(0);

    tierlisten: Tierliste[] = [];
    tierlisteData: Tierliste = undefined;
    tierlistenSubscription: Subscription;
    private showAbteilSub?: Subscription;
    private sortSub?: Subscription;

    downloadAsPdf = false;
    downloadAsExcel = false;
    downloadAsCsv = false;
    sortCtrl = new FormControl('t.ohrmarke');
    addImpfstoffeCtrl = new FormControl(0);
    showImpfstoffe = false;

    // Zielbetrieb-Auswahl
    zielbetriebe: Array<{ kunde_id: number; bezeichnung_final: string; zusatz_final: string }> = [];
    zielbetriebCtrl = new FormControl(null);
    showZielbetriebAuswahl = false;

    constructor(
        public dialogRef: MatDialogRef<GruppeAbgangDownloadDialogComponent>,
        @Inject(MAT_DIALOG_DATA) public data: any,
        private readonly messageService: MessageService,
        private readonly gruppenService: GruppenService,
        private readonly api: KieneBackendApiService,
        @Inject(API_BASE_URL_SERVICE) private apiBaseUrl: string,
        private readonly changeDetectorRef: ChangeDetectorRef,
    ) {
        if (this.data.tierliste) {
            this.tierlisteData = this.data.tierliste;
            this.ladeImpfstoffe([this.tierlisteData]);
            this.pruefeZielbetriebe(this.tierlisteData);
        } else if (data.gruppe?.gruppe_id) {
            this.ladeTierlisten(data.gruppe.gruppe_id);
        }

        this.tierlistCtrl.valueChanges.subscribe({
            next: (tl: Tierliste[]) => {
                this.ladeImpfstoffe(tl);
            },
        });

        this.showAbteilSub = this.showAbteilCtrl.valueChanges.subscribe({
            next: value => {
                if (!this.isShowAbteilValue(value) && this.sortCtrl.value === SORT_ABTEIL) {
                    this.sortCtrl.setValue('t.ohrmarke');
                }
                this.changeDetectorRef.markForCheck();
            },
        });

        this.sortSub = this.sortCtrl.valueChanges.subscribe({
            next: value => {
                // Sort by Abteil always implies showing the Abteil column.
                if (value === SORT_ABTEIL && !this.isShowAbteilValue(this.showAbteilCtrl.value)) {
                    this.showAbteilCtrl.setValue(1);
                }
                this.changeDetectorRef.markForCheck();
            },
        });
    }

    pruefeZielbetriebe(tierliste: Tierliste) {
        // Prüfe ob mehrere Zielbetriebe vorhanden sind (aus betriebskontakte_tierliste)
        if (tierliste?.kunde_id_zielbetrieb) {
            const zielbetriebIds = tierliste.kunde_id_zielbetrieb.toString().split(',');
            if (zielbetriebIds.length > 1) {
                this.showZielbetriebAuswahl = true;
                this.ladeZielbetriebe(tierliste.tierliste_id);
            }
        }
    }

    ladeZielbetriebe(tierlisteId: number) {
        // Lade die Betriebskontakte aus der betriebskontakte_tierliste Tabelle
        let params = new HttpParams().set('tierliste_id', tierlisteId.toString());

        this.api.getAll(this.apiBaseUrl + 'benutzer_betriebskontakte_tierliste/read.php', params).subscribe({
            next: (response: KieneApi) => {
                this.zielbetriebe = response.records.map((record: any) => ({
                    kunde_id: record.kunden_id,
                    bezeichnung_final: record.bezeichnung_final,
                    zusatz_final: record.zusatz_final,
                }));

                // Immer den ersten auswählen (bei 1 Kontakt automatisch, bei mehreren als Default)
                if (this.zielbetriebe.length > 0) {
                    this.zielbetriebCtrl.setValue(this.zielbetriebe[0].kunde_id);
                }
            },
            error: err => {
                this.messageService.errorMessage('Fehler beim Laden der Zielbetriebe!', err);
            },
        });
    }

    ladeImpfstoffe(tierlisten: Tierliste[]) {
        this.showImpfstoffe = false;
        if (tierlisten?.length > 0) {
            let params = new HttpParams();
            for (const t of tierlisten) {
                params = params.append('tierliste_id[]', t.tierliste_id);
            }
            this.api.getAll(this.apiBaseUrl + 'tierliste/read_impfstoffe.php', params).subscribe({
                next: (response: KieneApi) => {
                    if (response.count > 0) {
                        this.showImpfstoffe = true;
                    }
                },
                error: err => {
                    this.messageService.errorMessage('Fehler!', err);
                },
            });
        }
    }

    ngOnDestroy(): void {
        this.tierlistenSubscription?.unsubscribe();
        this.showAbteilSub?.unsubscribe();
        this.sortSub?.unsubscribe();
    }

    ladeTierlisten(gruppe_id: number) {
        this.tierlistenSubscription?.unsubscribe();
        this.tierlistenSubscription = this.gruppenService.ladeTierlistenHistorie(gruppe_id).subscribe({
            next: response => {
                this.tierlisten = response.records;
                if (this.tierlisten.length === 1) {
                    this.tierlistCtrl.setValue(this.tierlisten, { emitEvent: false }); // Liste
                    this.ladeImpfstoffe(this.tierlisten);
                }
            },
            error: err => {
                this.messageService.errorMessage('Fehler!', err);
            },
        });
    }

    downloadFiles() {
        if (this.downloadAsCsv === false && this.downloadAsExcel === false && this.downloadAsPdf === false) {
            this.messageService.alertMessage('Bitte mindestens einen Dateitypen auswählen!');
            return;
        }

        let tierlisten_ids = [];
        if (this.tierlisteData) {
            tierlisten_ids.push(this.tierlisteData.tierliste_id);
        } else {
            if (this.tierlistCtrl.value) {
                for (const tierliste of this.tierlistCtrl.value) {
                    tierlisten_ids.push(tierliste.tierliste_id);
                }
            }
        }

        if (tierlisten_ids.length === 0) {
            this.messageService.alertMessage('Bitte mindestens eine Liste auswählen!');
            return;
        }

        const sortierung = this.sortCtrl.value || 't.ohrmarke';
        const showAbteil = this.isShowAbteilValue(this.showAbteilCtrl.value) || sortierung === SORT_ABTEIL ? 1 : 0;

        const result = new AbgangDownloadRequestPojo(
            tierlisten_ids,
            this.showBarcodeCtrl.value,
            this.showGruppendetailsCtrl.value,
            this.showTiergruppeCtrl.value,
            this.addScansCtrl.value,
            this.addImpfstoffeCtrl.value,
            this.showEinstalldatumCtrl.value,
            Number(this.showEinstalldatumCtrl.value) === 1 ? this.einstalldatumSortCtrl.value || 'none' : 'none',
            showAbteil,
        );
        result.csv = this.downloadAsCsv;
        result.excel = this.downloadAsExcel;
        result.pdf = this.downloadAsPdf;
        result.sortierung = showAbteil ? sortierung : this.sortOhneAbteil(sortierung);
        result.geschlecht_sort = this.geschlechtSortCtrl.value || 'none';
        result.split_geschlecht = this.downloadAsPdf && Number(this.splitGeschlechtCtrl.value) === 1;

        // Zielbetrieb mitgeben wenn PDF ausgewählt und Zielbetriebe vorhanden sind
        // Bei nur 1 Zielbetrieb: automatisch verwenden
        // Bei mehreren Zielbetrieben: den ausgewählten verwenden
        if (this.downloadAsPdf && this.showZielbetriebAuswahl && this.zielbetriebe.length > 0 && this.zielbetriebCtrl.value) {
            result.zielbetrieb_id = this.zielbetriebCtrl.value;
        }

        this.dialogRef.close(result);
    }

    cancel() {
        this.dialogRef.close();
    }

    showAbteilAnzeigen(): boolean {
        return this.isShowAbteilValue(this.showAbteilCtrl.value);
    }

    private isShowAbteilValue(value: unknown): boolean {
        return Number(value) > 0;
    }

    private sortOhneAbteil(sortierung: string): string {
        return sortierung === SORT_ABTEIL ? 't.ohrmarke' : sortierung;
    }

    compareTierliste(a: Tierliste, b: Tierliste) {
        return a && b && KieneUtils.sameId(a.tierliste_id, b.tierliste_id);
    }
}
